import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Eye, Info, Pencil, X } from "lucide-react";
import CertificateDocument from "./CertificateDocument";
import {
  useGenerateCertificateMutation,
  useGetCertificatePreviewQuery,
} from "../../services/certificate";

const CertificatePreviewModal = ({
  open,
  onClose,
  onRequestNameCorrection,
  enrollmentId,
}) => {
  const certificateRef = useRef(null);
  const [generatedCertificate, setGeneratedCertificate] = useState(null);
  const [generateError, setGenerateError] = useState("");

  const {
    data: previewResponse,
    isLoading,
    isError,
    error,
  } = useGetCertificatePreviewQuery(enrollmentId, {
    skip: !open || !enrollmentId,
  });

  const [generateCertificate, { isLoading: isGenerating }] =
    useGenerateCertificateMutation();

  const preview = previewResponse?.data;
  const certificateId =
    generatedCertificate?.certificateId ||
    generatedCertificate?.certificateNumber ||
    generatedCertificate?._id ||
    null;
  const isGenerated = Boolean(certificateId);

  useEffect(() => {
    if (!open) {
      setGeneratedCertificate(null);
      setGenerateError("");
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const handleRequestNameCorrection = () => {
    onClose();
    onRequestNameCorrection?.();
  };

  const handleGenerate = async () => {
    if (!enrollmentId || isGenerated) return;

    setGenerateError("");

    try {
      const response = await generateCertificate(enrollmentId).unwrap();

      if (response?.success === false) {
        setGenerateError(
          response?.message || "Unable to generate certificate right now.",
        );
        return;
      }

      setGeneratedCertificate(response?.data ?? response);
    } catch (error) {
      setGenerateError(
        error?.data?.message ||
          error?.message ||
          "Unable to generate certificate right now.",
      );
    }
  };

  const handleViewCertificate = () => {
    certificateRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/40 px-4 py-8 sm:py-10">
      <div className="flex h-full max-h-[calc(100vh-4rem)] w-full max-w-[896px] flex-col overflow-hidden rounded-xl bg-white font-['Roboto']">
        <div className="flex shrink-0 items-center justify-between border-b border-[#E5E7EB] p-6">
          <h2 className="text-2xl font-bold text-[#111827]">
            Certificate Preview
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close certificate preview"
            className="text-[#6B7280] transition-opacity hover:opacity-70"
          >
            <X size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <div className="flex gap-3 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] p-[17px]">
            <Info size={20} className="mt-1 shrink-0 text-[#3B82F6]" />
            <div>
              <p className="text-base font-medium text-[#1E3A8A]">
                Auto-fetched Information
              </p>
              <p className="mt-1 text-sm text-[#1D4ED8]">
                Your name is automatically fetched from your registration
                details. If you need to make corrections, please use the
                &quot;Request Name Correction&quot; option below.
              </p>
            </div>
          </div>

          {isLoading ? (
            <p className="mt-8 text-center text-base text-[#6B7280]">
              Loading certificate preview...
            </p>
          ) : null}

          {isError ? (
            <p className="mt-8 text-center text-base text-[#EF4444]">
              {error?.data?.message || "Could not load certificate preview."}
            </p>
          ) : null}

          {!isLoading && !isError && preview ? (
            <>
              <div ref={certificateRef}>
                <CertificateDocument
                  className="mt-6"
                  studentName={preview.studentName}
                  courseTitle={preview.courseTitle}
                  grade={preview.grade}
                  completionDate={preview.completionDate}
                  certificateId={certificateId}
                  isVerified={isGenerated}
                />
              </div>

              <div className="mt-8 flex flex-col items-center justify-center gap-4 pb-2">
                {generateError ? (
                  <div className="flex w-full gap-3 rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-4">
                    <AlertCircle
                      size={20}
                      className="mt-0.5 shrink-0 text-[#EF4444]"
                    />
                    <div>
                      <p className="text-base font-medium text-[#991B1B]">
                        Certificate not generated
                      </p>
                      <p className="mt-1 text-sm text-[#B91C1C]">
                        {generateError}
                      </p>
                    </div>
                  </div>
                ) : null}

                <div className="flex w-full flex-col items-center justify-center gap-4 sm:flex-row">
                <button
                  type="button"
                  onClick={handleRequestNameCorrection}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-[#923D8C] px-6 py-3 text-base font-medium text-[#923D8C] transition-colors hover:bg-[#923D8C0D]"
                >
                  <Pencil size={18} />
                  Request Name Correction
                </button>

                {isGenerated ? (
                  <button
                    type="button"
                    onClick={handleViewCertificate}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#F97316] to-[#9333EA] px-6 py-3 text-base font-medium text-white transition-colors hover:opacity-90"
                  >
                    <Eye size={18} />
                    View Certificate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#F97316] to-[#9333EA] px-6 py-3 text-base font-medium text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isGenerating ? "Generating..." : "Generate Certificate"}
                  </button>
                )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CertificatePreviewModal;
